import { useEffect, useState } from 'react';
import { Button, Typography, Box, Stack, IconButton, Chip, Divider, Tooltip } from '@mui/material';
import { Grid } from '@mui/material';
import { updateBusinessStatus, updateStaffStatus, getStylistShowcaseCount, getLastListingDate, getWeeklyShowcaseCount, getCampaigns, updateUserFlag } from '@services/profileService';
import type { Campaign } from '@app-types/models/user';
import { getStylists } from '@services/stylistService';
import type { Stylist } from '@app-types/models/stylist';
import Mission from '@components/Mission';
import QuickListingWizard from '@components/QuickListingWizard';
import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import { useTranslation } from 'react-i18next';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import DateRangeIcon from '@mui/icons-material/DateRange';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PeopleIcon from '@mui/icons-material/People';
import TollIcon from '@mui/icons-material/Toll';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import DashboardIcon from '@mui/icons-material/Dashboard';
import { QRCodeCanvas } from 'qrcode.react';
import { capitalize } from 'lodash';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PlatformGrowth from '@components/PlatformGrowth';
import { generateReviewQr } from '@services/liveReviewService';
import QuickReviewModal from '../components/reservation/QuickReviewModal';
import { PageContainer, PageHeader, SectionCard, StatCard } from '../components/ui';
import { brand } from '../theme/tokens';

type DashboardProps = WithUserProps;

const Dashboard = ({ user, fetchUserData }: DashboardProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const dashUser = user;
  const [showcaseCount, setShowcaseCount] = useState<number>(0);
  const [lastListingDate, setLastListingDate] = useState<string | null>(null);
  const [timeUntilNextWeek, setTimeUntilNextWeek] = useState<string>('');
  const [weeklyShowcaseCount, setWeeklyShowcaseCount] = useState<number>(0);
  const [numberOfWeeksHasPastBy, setNumberOfWeeksHasPastBy] = useState<number>(0);
  const [listingStylistsCount, setListingStylistsCount] = useState<number>(0);
  const [stylists, setStylists] = useState<Stylist[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [qrCodeOpen, setQrCodeOpen] = useState<boolean>(false);
  const [qrCodeValue, setQrCodeValue] = useState<string>('');
  const [qrCodeTitleKey, setQrCodeTitleKey] = useState<string>('dashboard.qrCodeBusiness');
  const [quickListingWizardOpen, setQuickListingWizardOpen] = useState<boolean>(false);
  const [activeWizardStep, setActiveWizardStep] = useState<number>(0);
  const [initialStylistIdForWizard, setInitialStylistIdForWizard] = useState<string | null>(null);
  const [firstTimeLoginDialogOpen, setFirstTimeLoginDialogOpen] = useState<boolean>(false);
  const [walkInQrOpen, setWalkInQrOpen] = useState<boolean>(false);
  const [walkInQrUrl, setWalkInQrUrl] = useState<string>('');
  const [walkInQrLoading, setWalkInQrLoading] = useState<boolean>(false);
  const [walkinModalOpen, setWalkinModalOpen] = useState<boolean>(false);

  useEffect(() => {
    if (dashUser) {
      if (dashUser.flags && dashUser.flags.firstTimeLogin) {
        console.log('Dashboard: First time login detected.');
        setFirstTimeLoginDialogOpen(true);
        updateUserFlag('firstTimeLogin', false)
          .then(() => {
            console.log('Successfully updated firstTimeLogin flag to false');
          })
          .catch((error: unknown) => {
            console.error('Failed to update firstTimeLogin flag:', error);
          });
      }

      if (dashUser.role === 'stylist') {
        getStylistShowcaseCount(dashUser._id).then((count) => setShowcaseCount(count)).catch((error: unknown) => console.error('Error fetching showcase count:', error));
        getLastListingDate().then((response) => {
          const date = response.lastListingDate;
          setLastListingDate(date);
          if (date) calculateTimeUntilNextWeek(date);
        }).catch((error: unknown) => console.error('Error fetching last listing date:', error));
        getWeeklyShowcaseCount().then((response) => {
          setWeeklyShowcaseCount(response.currentWeekCount ?? 0);
          setNumberOfWeeksHasPastBy(response.weeksPassed ?? 0);
        }).catch((error: unknown) => console.error('Error fetching weekly showcase count:', error));
      } else if (dashUser.businessName) {
        getStylists().then((result) => {
          const stylistsData = Array.isArray(result) ? result : [];
          const listingStylistsCount = stylistsData.filter(stylist => stylist.status === 'listing').length;
          setListingStylistsCount(listingStylistsCount);
          // Also stash the full list so the Quick Live Review modal can offer a stylist picker.
          setStylists(stylistsData);
        }).catch((error: unknown) => console.error('Error fetching stylists:', error));
      }
      getCampaigns().then((result) => setCampaigns(Array.isArray(result) ? result : [])).catch((error: unknown) => console.error('Error fetching campaigns:', error));
    }
  }, [dashUser]);

  // Helper function to clean up wizard state
  const cleanupWizardState = () => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('wizard') || url.searchParams.has('step') || url.searchParams.has('instagram_success')) {
      url.searchParams.delete('wizard');
      url.searchParams.delete('step');
      url.searchParams.delete('instagram_success');
      window.history.replaceState({}, '', url.toString());
    }
    sessionStorage.removeItem('wizard_open');
    sessionStorage.removeItem('wizard_step');
    sessionStorage.removeItem('wizard_stylist_id');
    console.log('Dashboard: Cleaned URL and sessionStorage.');
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const wizardParam = searchParams.get('wizard');
    const stepParam = searchParams.get('step');
    const instagramSuccess = searchParams.get('instagram_success');

    if (wizardParam === 'true' && stepParam) {
      if (instagramSuccess === 'true') {
        console.log('Dashboard: Skipping wizard - user completed Instagram onboarding');
        cleanupWizardState();
        return;
      }

      console.log('Dashboard: Detected redirect with wizard params. Attempting to open wizard.');

      if (!dashUser?.flags?.quickSetupWizardDone && !(location.state as Record<string, unknown> | null)?.newUser) {
        const stepNumber = parseInt(stepParam, 10);
        const restoredStylistId = sessionStorage.getItem('wizard_stylist_id');

        setActiveWizardStep(stepNumber);
        if (restoredStylistId) {
          setInitialStylistIdForWizard(restoredStylistId);
          console.log('Dashboard: Restored stylistId from session:', restoredStylistId);
        }
        setQuickListingWizardOpen(true);
      } else {
        console.log('Dashboard: Quick setup wizard already completed, not reopening after redirect.');
      }

      cleanupWizardState();
    }
  }, [location, dashUser?.flags?.quickSetupWizardDone]);

  const handleWizardClose = () => {
    setQuickListingWizardOpen(false);
    setActiveWizardStep(0);
    setInitialStylistIdForWizard(null);
    cleanupWizardState();
  };

  const handleStartListing = async () => {
    const response = await updateBusinessStatus({ status: 'listing' });
    console.log('response', response);
    fetchUserData();
  };

  const handleStopListing = async () => {
    const response = await updateBusinessStatus({ status: 'approved' });
    console.log('response', response);
    fetchUserData();
  };

  const handleStartListingStylist = async () => {
    const response = await updateStaffStatus({ staffId: dashUser._id, status: 'listing' });
    console.log('response', response);
    fetchUserData();
  };

  const handleStopListingStylist = async () => {
    const response = await updateStaffStatus({ staffId: dashUser._id, status: 'suspended' });
    console.log('response', response);
    fetchUserData();
  };

  const calculateTimeUntilNextWeek = (lastDate: string) => {
    const lastListing = new Date(lastDate);
    const nextWeek = new Date(lastListing);
    nextWeek.setDate(lastListing.getDate() + 7);

    const updateCountdown = () => {
      const now = new Date();
      const timeDifference = nextWeek.getTime() - now.getTime();
      if (timeDifference > 0) {
        const days = Math.floor(timeDifference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((timeDifference / (1000 * 60 * 60)) % 24);
        const minutes = Math.floor((timeDifference / (1000 * 60)) % 60);
        const seconds = Math.floor((timeDifference / 1000) % 60);
        setTimeUntilNextWeek(`${days}d ${hours}h ${minutes}m ${seconds}s`);
      } else {
        setTimeUntilNextWeek('Time to upload new showcases!');
      }
    };

    updateCountdown();
    const intervalId = setInterval(updateCountdown, 1000);
    return () => clearInterval(intervalId);
  };

  const handleOpenQrCodeDialog = (value: string, type: string) => {
    setQrCodeValue(value);
    setQrCodeTitleKey(type === 'business' ? 'dashboard.qrCodeBusiness' : 'dashboard.qrCodeCustomer');
    setQrCodeOpen(true);
  };

  const handleCloseQrCodeDialog = () => setQrCodeOpen(false);
  const handleFirstTimeLoginDialogClose = () => setFirstTimeLoginDialogOpen(false);

  const handleStartQuickListingWizard = () => {
    setFirstTimeLoginDialogOpen(false);
    setQuickListingWizardOpen(true);
  };

  const handleOpenWalkInQr = async () => {
    setWalkInQrOpen(true);
    setWalkInQrLoading(true);
    try {
      const { reviewUrl } = await generateReviewQr();
      setWalkInQrUrl(reviewUrl);
    } catch (error: unknown) {
      console.error('Error generating walk-in review QR:', error);
      toast.error(t('liveReview.qrError'));
      setWalkInQrOpen(false);
    } finally {
      setWalkInQrLoading(false);
    }
  };

  const handleCloseWalkInQr = () => {
    setWalkInQrOpen(false);
    setWalkInQrUrl('');
  };

  const handleDownloadWalkInQr = () => {
    const canvas = document.getElementById('walk-in-review-qr') as HTMLCanvasElement | null;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = 'walk-in-review-qr.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  // --- Revamp helpers ---
  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success(t('common.copied', 'Copied to clipboard'));
  };

  const businessReferralUrl = `${import.meta.env.VITE_BASE_URL}/onboarding?referral_code=${dashUser?.referralCode}`;
  const customerReferralUrl = `${import.meta.env.VITE_SITE_URL}/#/account/signup?referral_code=${dashUser?.referralCode}`;
  const isListing = dashUser?.status === 'listing';

  const statusChip = (
    <Chip
      icon={isListing ? <CheckCircleIcon /> : undefined}
      label={capitalize(dashUser?.status || '—')}
      color={isListing ? 'success' : 'default'}
      size="small"
      sx={{ fontWeight: 600 }}
    />
  );

  const ReferralRow = ({ label, url, type }: { label: string; url: string; type: 'business' | 'customer' }) => (
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: brand.cream, border: `1px solid ${brand.border}` }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Typography
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        sx={{ display: 'block', color: 'primary.dark', wordBreak: 'break-all', my: 0.5, textDecoration: 'none' }}
      >
        {url}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={() => copy(url)}>
          {t('common.copy', 'Copy')}
        </Button>
        <Button size="small" variant="outlined" startIcon={<QrCodeIcon />} onClick={() => handleOpenQrCodeDialog(url, type)}>
          {t('dashboard.qrCode', 'QR')}
        </Button>
      </Stack>
    </Box>
  );

  return (
    dashUser && (
      <PageContainer>
        <PageHeader
          title={t('dashboard.welcome', { name: dashUser.displayName || dashUser.businessName || t('dashboard.stylist') })}
          subtitle={t('dashboard.dashboardSubtitle', "Here's an overview of your salon today.")}
          icon={<DashboardIcon />}
          classicPath="/classic/dashboard"
        />

        <Box sx={{ mb: 2.5 }}>
          <PlatformGrowth />
        </Box>

        {/* Quick stats */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          {dashUser.role === 'stylist' ? (
            <>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard tone="gold" icon={<PhotoLibraryIcon />} label={t('dashboard.manageShowcases')} value={showcaseCount} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard tone="sage" icon={<DateRangeIcon />} label={t('dashboard.thisWeek', 'This week')} value={weeklyShowcaseCount} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard tone="navy" icon={<CheckCircleIcon />} label={t('dashboard.stylistStatus')} value={<Typography variant="body1" sx={{ fontWeight: 700 }}>{capitalize(dashUser.status || '—')}</Typography>} />
              </Grid>
              {typeof dashUser.credit === 'number' && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard tone="rust" icon={<TollIcon />} label={t('dashboard.credit')} value={dashUser.credit} />
                </Grid>
              )}
            </>
          ) : (
            <>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard tone="gold" icon={<PeopleIcon />} label={t('dashboard.manageStylists')} value={listingStylistsCount} hint={t('dashboard.listingLabel', 'listing now')} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <StatCard tone="navy" icon={<CheckCircleIcon />} label={t('dashboard.businessStatus')} value={<Typography variant="body1" sx={{ fontWeight: 700 }}>{capitalize(dashUser.status || '—')}</Typography>} />
              </Grid>
              {typeof dashUser.credit === 'number' && (
                <Grid size={{ xs: 6, sm: 3 }}>
                  <StatCard tone="rust" icon={<TollIcon />} label={t('dashboard.credit')} value={dashUser.credit} />
                </Grid>
              )}
            </>
          )}
        </Grid>

        {/* Quick setup prompt */}
        {(!dashUser.flags || !dashUser.flags.quickSetupWizardDone) && !(location.state as Record<string, unknown> | null)?.newUser && (
          <Box
            sx={{
              mb: 2.5,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 3,
              color: '#fff',
              background: 'linear-gradient(135deg, #232122 0%, #4D4A4B 100%)',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: 2,
            }}
          >
            <RocketLaunchIcon sx={{ fontSize: 40, color: brand.goldLight, flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ color: '#fff' }}>
                {t('dashboard.quickListingWizard')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {t('dashboard.quickListingWizardDescription', 'Help you quickly listing your stylist on hkstylist platform')}
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={() => setQuickListingWizardOpen(true)} sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
              {t('dashboard.getStarted', 'Get started')}
            </Button>
          </Box>
        )}

        <Grid container spacing={2}>
          {/* Account + referrals */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard title={t('dashboard.userInfo')} icon={<PeopleIcon />} sx={{ height: '100%' }}>
              <Stack spacing={1} sx={{ mb: 2 }}>
                <InfoLine label={t('dashboard.name')} value={dashUser.displayName || dashUser.businessName || '—'} />
                <InfoLine label={t('dashboard.email')} value={dashUser.email} />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {t('dashboard.referralCode')}:
                  </Typography>
                  <Chip label={dashUser.referralCode || '—'} size="small" variant="outlined" />
                  <Tooltip title={t('common.copy', 'Copy')}>
                    <IconButton onClick={() => copy(dashUser.referralCode || '')} size="small">
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Stack>
              </Stack>

              <Divider sx={{ mb: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('dashboard.shareInvites', 'Share your invite links')}
              </Typography>
              <Stack spacing={1.5}>
                <ReferralRow label={t('dashboard.referralLinkBusiness')} url={businessReferralUrl} type="business" />
                <ReferralRow label={t('dashboard.referralLinkCustomer')} url={customerReferralUrl} type="customer" />
              </Stack>
            </SectionCard>
          </Grid>

          {/* Status + listing actions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2} sx={{ height: '100%' }}>
              {(dashUser.role === 'stylist' || dashUser.businessName) && (
                <SectionCard
                  title={t('liveReview.title')}
                  icon={<QrCodeIcon />}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <Button fullWidth variant="contained" startIcon={<QrCodeIcon />} onClick={() => setWalkinModalOpen(true)}>
                          {t('liveReview.walkinQr')}
                        </Button>
                        <Button fullWidth variant="outlined" startIcon={<QrCodeIcon />} onClick={handleOpenWalkInQr}>
                          {t('liveReview.myReviewQr')}
                        </Button>
                      </Stack>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                        {t('liveReview.walkinQrHint')}
                      </Typography>
                    </Box>
                  </Stack>
                </SectionCard>
              )}

              {dashUser.role === 'stylist' && (
                <SectionCard
                  title={t('dashboard.stylistStatus')}
                  icon={<AutoAwesomeIcon />}
                  action={statusChip}
                >
                  <Stack spacing={1.5}>
                    {isListing ? (
                      <Stack spacing={1.25}>
                        <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>
                          {t('dashboard.listingCongrats')}
                        </Typography>
                        {lastListingDate && (
                          <Typography variant="body2" color="text.secondary">
                            {t('dashboard.lastListingDate')} <strong>{new Date(lastListingDate).toLocaleString()}</strong>
                          </Typography>
                        )}
                        <Button variant="outlined" color="inherit" onClick={handleStopListingStylist}>
                          {t('dashboard.stopListing')}
                        </Button>
                      </Stack>
                    ) : (
                      <Stack spacing={1.25}>
                        <Typography variant="subtitle2">{t('dashboard.nextStep')}</Typography>
                        <Typography variant="body2" color="text.secondary">{t('dashboard.notListing')}</Typography>
                        <Typography variant="body2">{t('dashboard.validShowcases', { count: showcaseCount })}</Typography>
                        <Button variant="contained" onClick={handleStartListingStylist}>{t('dashboard.startListing')}</Button>
                      </Stack>
                    )}
                  </Stack>
                </SectionCard>
              )}

              {dashUser.businessName && (
                <SectionCard title={t('dashboard.businessStatus')} icon={<AutoAwesomeIcon />} action={statusChip}>
                  {isListing ? (
                    <Stack spacing={1.5}>
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 600 }}>{t('dashboard.businessCongrats')}</Typography>
                      <Button variant="outlined" color="inherit" onClick={handleStopListing}>{t('dashboard.stopListing')}</Button>
                    </Stack>
                  ) : (
                    <Stack spacing={1.5}>
                      <Typography variant="subtitle2">{t('dashboard.nextStep')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('dashboard.businessNotListing', { count: listingStylistsCount })}</Typography>
                      {listingStylistsCount > 0 && (
                        <Button variant="contained" onClick={handleStartListing}>{t('dashboard.startListing')}</Button>
                      )}
                      <Button variant="outlined" href={`${import.meta.env.VITE_BASE_URL}/stylist`}>
                        {t('common.goToStylistPage')}
                      </Button>
                    </Stack>
                  )}
                </SectionCard>
              )}

              <SectionCard title={t('dashboard.mission', 'Weekly mission')} icon={<RocketLaunchIcon />} sx={{ flexGrow: 1 }}>
                <Mission
                  numberOfWeeksHasPastBy={numberOfWeeksHasPastBy}
                  weeklyShowcaseCount={weeklyShowcaseCount}
                  timeUntilNextWeek={timeUntilNextWeek}
                  campaigns={campaigns}
                  user={user}
                />
              </SectionCard>
            </Stack>
          </Grid>
        </Grid>

        {/* --- Dialogs (unchanged behaviour) --- */}
        <Dialog open={qrCodeOpen} onClose={handleCloseQrCodeDialog}>
          <DialogTitle>{t(qrCodeTitleKey)}</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Box sx={{ p: 2, bgcolor: '#fff', display: 'inline-block' }}>
              <QRCodeCanvas value={qrCodeValue} size={256} />
            </Box>
          </DialogContent>
        </Dialog>

        <QuickReviewModal open={walkinModalOpen} onClose={() => setWalkinModalOpen(false)} reservation={null} walkin stylists={stylists} />

        <Dialog open={walkInQrOpen} onClose={handleCloseWalkInQr} maxWidth="xs" fullWidth>
          <DialogTitle>{t('liveReview.myReviewQr')}</DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            {walkInQrLoading || !walkInQrUrl ? (
              <Typography sx={{ py: 4 }}>{t('common.loading')}</Typography>
            ) : (
              <>
                <Box sx={{ display: 'inline-block', p: 2, bgcolor: '#fff' }}>
                  <QRCodeCanvas id="walk-in-review-qr" value={walkInQrUrl} size={256} />
                </Box>
                <Typography variant="body2" sx={{ mt: 1 }}>{t('liveReview.walkInInstruction')}</Typography>
                <Typography
                  variant='body2'
                  component='a'
                  href={walkInQrUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{
                    mt: 1.5,
                    display: 'inline-block',
                    color: 'primary.main',
                    fontWeight: 600,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {t('liveReview.qr.openInNewTab')}
                </Typography>
              </>
            )}
          </DialogContent>
          <DialogActions>
            {walkInQrUrl && (
              <Button startIcon={<DownloadIcon />} onClick={handleDownloadWalkInQr}>{t('liveReview.download')}</Button>
            )}
            <Button onClick={handleCloseWalkInQr}>{t('common.close')}</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={firstTimeLoginDialogOpen} onClose={handleFirstTimeLoginDialogClose} aria-labelledby="first-time-login-dialog-title">
          <DialogTitle id="first-time-login-dialog-title">{t('dashboard.welcomeFirstTime')}</DialogTitle>
          <DialogContent>
            <DialogContentText>{t('dashboard.firstTimeLoginMessage')}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleFirstTimeLoginDialogClose} color="primary">{t('common.later')}</Button>
            <Button onClick={handleStartQuickListingWizard} color="primary" variant="contained" autoFocus>{t('dashboard.getStarted')}</Button>
          </DialogActions>
        </Dialog>

        <QuickListingWizard
          open={quickListingWizardOpen}
          onClose={handleWizardClose}
          fetchUserData={fetchUserData}
          initialStep={activeWizardStep}
          initialStylistId={initialStylistIdForWizard}
        />
      </PageContainer>
    )
  );
};

const InfoLine = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, flexShrink: 0 }}>
      {label}:
    </Typography>
    <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Stack>
);

const WrappedDashboard = withUser(Dashboard);
export default WrappedDashboard;
